import React, { useState, useEffect } from 'react';
import { Package, ShoppingCart, Mail, DollarSign, RefreshCw, Eye, Users, Globe, TrendingUp, BarChart3, MousePointer } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const StatsTab = ({ stats, onRefresh }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [period, setPeriod] = useState('30');

  useEffect(() => { fetchAnalytics(); }, [period]);

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/analytics/summary?days=${period}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setAnalytics(await res.json());
    } catch {}
    finally { setLoadingAnalytics(false); }
  };

  if (!stats) return null;

  const statCards = [
    { title: 'Total Products', value: stats.total_products, subtitle: `${stats.in_stock} in stock`, icon: Package, color: '#FF8C19' },
    { title: 'Total Orders', value: stats.total_orders, subtitle: 'All time', icon: ShoppingCart, color: '#22c55e' },
    { title: 'Revenue', value: `$${stats.total_revenue.toFixed(2)}`, subtitle: 'All time', icon: DollarSign, color: '#3b82f6' },
    { title: 'Messages', value: stats.total_contacts, subtitle: `${stats.new_contacts} new`, icon: Mail, color: '#a855f7' },
  ];

  const maxViews = analytics?.dailyStats?.length ? Math.max(...analytics.dailyStats.map(d => d.views)) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold" style={{ color: '#4a2c1a' }}>Dashboard</h2>
        <Button onClick={() => { onRefresh(); fetchAnalytics(); }} variant="outline" className="border-2" style={{ borderColor: '#FF8C19', color: '#FF8C19' }}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Business Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white rounded-lg shadow-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${stat.color}20` }}>
                <stat.icon className="h-5 w-5" style={{ color: stat.color }} />
              </div>
            </div>
            <p className="text-xs font-medium" style={{ color: '#736c64' }}>{stat.title}</p>
            <p className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>{stat.value}</p>
            <p className="text-xs" style={{ color: '#736c64' }}>{stat.subtitle}</p>
          </div>
        ))}
      </div>

      {/* Analytics Section */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold flex items-center" style={{ color: '#4a2c1a' }}>
          <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#FF8C19' }} /> Site Analytics
        </h3>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loadingAnalytics ? (
        <p className="text-center py-8" style={{ color: '#736c64' }}>Loading analytics...</p>
      ) : analytics ? (
        <>
          {/* Analytics Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-5">
              <div className="flex items-center mb-2">
                <Eye className="h-5 w-5 mr-2" style={{ color: '#6366f1' }} />
                <span className="text-xs font-medium" style={{ color: '#736c64' }}>Page Views</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>{analytics.totalViews.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-5">
              <div className="flex items-center mb-2">
                <Users className="h-5 w-5 mr-2" style={{ color: '#0ea5e9' }} />
                <span className="text-xs font-medium" style={{ color: '#736c64' }}>Unique Visitors</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>{analytics.uniqueVisitors.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-5">
              <div className="flex items-center mb-2">
                <MousePointer className="h-5 w-5 mr-2" style={{ color: '#f59e0b' }} />
                <span className="text-xs font-medium" style={{ color: '#736c64' }}>Sessions</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>{analytics.uniqueSessions.toLocaleString()}</p>
            </div>
          </div>

          {/* Daily Chart */}
          {analytics.dailyStats.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h4 className="text-sm font-bold mb-4 flex items-center" style={{ color: '#4a2c1a' }}>
                <TrendingUp className="h-4 w-4 mr-2" style={{ color: '#22c55e' }} /> Daily Traffic
              </h4>
              <div className="flex items-end gap-1" style={{ height: 160 }}>
                {analytics.dailyStats.map((day, i) => {
                  const h = maxViews > 0 ? (day.views / maxViews) * 140 : 0;
                  const dateLabel = new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center group" style={{ minWidth: 0 }}>
                      <div className="relative w-full">
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                          {dateLabel}: {day.views} views, {day.visitors} visitors
                        </div>
                        <div
                          className="w-full rounded-t transition-all hover:opacity-80"
                          style={{ height: Math.max(h, 2), backgroundColor: '#FF8C19' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-xs" style={{ color: '#736c64' }}>
                  {analytics.dailyStats.length > 0 ? new Date(analytics.dailyStats[0].date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                </span>
                <span className="text-xs" style={{ color: '#736c64' }}>
                  {analytics.dailyStats.length > 0 ? new Date(analytics.dailyStats[analytics.dailyStats.length - 1].date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                </span>
              </div>
            </div>
          )}

          {/* Tables Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Pages */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="text-sm font-bold mb-4 flex items-center" style={{ color: '#4a2c1a' }}>
                <Eye className="h-4 w-4 mr-2" style={{ color: '#6366f1' }} /> Top Pages
              </h4>
              {analytics.topPages.length === 0 ? (
                <p className="text-sm" style={{ color: '#736c64' }}>No data yet</p>
              ) : (
                <div className="space-y-3">
                  {analytics.topPages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm truncate mr-2" style={{ color: '#4a2c1a' }}>{p.page}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#6366f120', color: '#6366f1' }}>
                          {p.views}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Products */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="text-sm font-bold mb-4 flex items-center" style={{ color: '#4a2c1a' }}>
                <Package className="h-4 w-4 mr-2" style={{ color: '#FF8C19' }} /> Top Products
              </h4>
              {analytics.topProducts.length === 0 ? (
                <p className="text-sm" style={{ color: '#736c64' }}>No data yet</p>
              ) : (
                <div className="space-y-3">
                  {analytics.topProducts.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm truncate mr-2" style={{ color: '#4a2c1a' }}>{p.productName || p.productId}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FF8C1920', color: '#FF8C19' }}>
                        {p.views} views
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Referrers */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="text-sm font-bold mb-4 flex items-center" style={{ color: '#4a2c1a' }}>
                <Globe className="h-4 w-4 mr-2" style={{ color: '#0ea5e9' }} /> Traffic Sources
              </h4>
              {analytics.topReferrers.length === 0 ? (
                <p className="text-sm" style={{ color: '#736c64' }}>No referrer data yet</p>
              ) : (
                <div className="space-y-3">
                  {analytics.topReferrers.map((r, i) => {
                    let label = r.referrer;
                    try { label = new URL(r.referrer).hostname; } catch {}
                    return (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm truncate mr-2" style={{ color: '#4a2c1a' }}>{label}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#0ea5e920', color: '#0ea5e9' }}>
                          {r.visits}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <p className="text-center py-8" style={{ color: '#736c64' }}>No analytics data available yet.</p>
      )}
    </div>
  );
};

export default StatsTab;
